/**
 * Request deduplication utility
 * Prevents duplicate API calls for the same request within a time window
 */

interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

class RequestDeduplication {
  private pendingRequests = new Map<string, PendingRequest>();
  private readonly DEDUPE_WINDOW = 1000; // 1 second

  /**
   * Request with deduplication
   * If the same request is made within DEDUPE_WINDOW, returns the existing promise
   */
  async request<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    // Check if same request is pending
    const pending = this.pendingRequests.get(key);

    if (pending) {
      const age = Date.now() - pending.timestamp;
      if (age < this.DEDUPE_WINDOW) {
        // Return existing promise
        return pending.promise as Promise<T>;
      } else {
        // Request is stale, remove it
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = fetcher();
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    // Clean up after request completes
    promise
      .then(() => {
        this.pendingRequests.delete(key);
      })
      .catch(() => {
        this.pendingRequests.delete(key);
      });

    return promise;
  }

  /**
   * Cancel a pending request
   */
  cancel(key: string) {
    this.pendingRequests.delete(key);
  }

  /**
   * Clear all pending requests
   */
  clear() {
    this.pendingRequests.clear();
  }

  /**
   * Get pending request count (for debugging)
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }
}

export const requestDeduplication = new RequestDeduplication();
